import { Box, Modal, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../../api/hooks/useColors";
import { controlKeys } from "../../../../../../../../api/queryKeys";
import IconButton from "../../../../../../../../components/buttons/IconButton";
import TextButton from "../../../../../../../../components/buttons/TextButton";
import CustomTooltip from "../../../../../../../../components/CustomTooltip";
import { Toast } from "../../../../../../../../components/Toast";
import { icons } from "../../../../../../../stack/environment/icons/iconExport";

export default function ActionButtons({ log, handleEdit }) {
  const colors = useColors();
  const { updateCommentResolveStatus, deleteComment, trackEvent } = useAPI();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);

  const resolveMutation = useMutation({
    mutationFn: () =>
      updateCommentResolveStatus(
        log.control_id,
        log.log_id,
        !Boolean(log.resolved_by_user_id)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: controlKeys.controlActivityLogs(log.control_id),
      });
    },
    onError: () => {
      if (log.resolved_by_user_id) Toast.error("Failed to open comment");
      else Toast.error("Failed to resolve comment");
    },
  });

  const handleResolve = () => {
    const action = log.resolved_by_user_id ? "unresolved" : "resolved";
    trackEvent(`User ${action} comment ${log.log_id}`);
    resolveMutation.mutate();
  };

  const deleteMutation = useMutation({
    mutationFn: () => deleteComment(log.control_id, log.log_id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: controlKeys.controlActivityLogs(log.control_id),
      });
      setShowModal(false);
    },
    onError: () => {
      Toast.error("Failed to delete comment");
    },
  });

  const handleDelete = () => {
    trackEvent(`User deleted comment ${log.log_id}`);
    deleteMutation.mutate();
  };

  const handleOpenModal = () => {
    trackEvent(`User opened the comment delete confirmation modal`);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    trackEvent("User closed the comment delete confirmation modal");
    setShowModal(false);
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={0.5}>
        <CustomTooltip title="Delete comment">
          <Box>
            <IconButton
              onClick={handleOpenModal}
              IconSVG={icons.DeleteBinIcon2}
              buttonStyle="standard"
              size="XS"
            />
          </Box>
        </CustomTooltip>
        <CustomTooltip
          title={log.resolved_by_user_id ? "Reopen comment" : "Resolve comment"}
        >
          <Box>
            <IconButton
              onClick={handleResolve}
              IconSVG={icons.ResolveIcon}
              buttonStyle="standard"
              size="XS"
              loading={resolveMutation.isPending}
            />
          </Box>
        </CustomTooltip>
        {/* TODO - Activity logs V2 */}
        {/* <IconButton
          onClick={() => {}}
          IconSVG={icons.EditIcon2}
          buttonStyle="standard"
          size="XS"
        /> */}
      </Box>
      <Modal open={showModal} onClose={handleCloseModal}>
        <Box
          width="400px"
          padding="24px 24px 12px 24px"
          position="absolute"
          borderRadius="10px"
          top="50%"
          left="50%"
          sx={{
            transform: "translate(-50%, -50%)",
            backgroundColor: colors.modal_bg,
            outline: "none",
          }}
        >
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="subtitle1" color={colors.text_primary}>
                Confirmation
              </Typography>

              <Typography variant="body2" color={colors.text_primary}>
                Are you sure you want to delete this comment?
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="end"
              alignItems="center"
              gap={1}
            >
              <TextButton
                onClick={handleCloseModal}
                color="inherit"
                disabled={deleteMutation.isPending}
                size="medium"
              >
                Cancel
              </TextButton>
              <TextButton
                onClick={handleDelete}
                color="error"
                size="medium"
                loading={deleteMutation.isPending}
              >
                Delete
              </TextButton>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
